import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import BlockIcon from '@mui/icons-material/Block';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LanguageIcon from '@mui/icons-material/Language';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Divider, Paper, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { actions } from '../../../actions';
import { truncate, last, isEmpty, isUndefined } from 'lodash';
import useChats from '../../../hooks/useChats';
import Pagination from '@mui/material/Pagination';

const useStyles = (small) => {
  return makeStyles((theme) => ({
    list: {
      paddingBottom: '0px',
      height: '100%',
      paddingTop: 0,
    },
    chatContainer: {
      position: 'relative',
      minHeight: '45px',
      display: 'flex',
      flexDirection: 'column',
      cursor: 'pointer',
      '&:hover': {
        background: '#dfdfdf',
      },
    },
    chatSuperText: {
      display: 'flex',
      fontSize: theme.sizes.sm.t1,
      position: 'absolute',
      bottom: '2px',
      width: 'calc(100% - 24px)',
      color: '#7a7a7a',
    },
    chipBox: {
      position: 'absolute',
      left: '8px',
      top: '16px',
      textAlign: 'right',
      padding: '0 8px',
      margin: '0px',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
    },
    chips: {
      fontSize: '0.5em',
      height: '18px',
      display: 'inline-flex',
      cursor: 'pointer',
      textTransform: 'uppercase',
      marginRight: '5px',
      color: 'white',
    },
    blockedChat: {
      fontSize: theme.icons.main.size,
      display: 'flex',
      alignItems: 'center',
      marginRight: '4px',
    },
    paper: {
      flex: 1,
      display: 'flex',
      overflowY: 'auto',
      overflowX: 'hidden',
      flexDirection: 'column',
      height: small ? 'calc(100dvh - 125px)' : '100%',
      '&::-webkit-scrollbar': {
        width: '1em',
      },
      '&::-webkit-scrollbar-track': {
        '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,.2)',
      },
    },
    chatActive: {
      background: '#dfdfdf',
    },
    whatsappIcon: {
      fontSize: theme.icons.main.size,
      color: 'green',
    },
    webchatIcon: {
      fontSize: theme.icons.main.size,
      color: 'blue',
    },
  }));
};

const ChatList = () => {
  const small = useMediaQuery('(max-width:600px)');
  const classes = useStyles(small)();
  const appDispatch = useDispatch();
  const chats = useChats();
  const activeChat = useSelector((state) => state.app.activeChat);
  const scrollRef = useRef();
  const config = useSelector((state) => state.app.prestobotsConfig);
  const [page, setPage] = useState(1);
  const showChatsNoEnded = useSelector((state) => state.app.activeChatShowChatsNoEnded);
  const [lastShowChatsNoEnded, setLastShowChatsNoEnded] = useState(showChatsNoEnded);
  const filter = useSelector((state) => state.app.filter);

  useEffect(() => {
    if (page !== 1) setPage(1);
  }, [filter.query.date, filter.query.keyword]);

  const handleClick = (id) => {
    appDispatch({
      type: actions.lastUpdate,
      lastUpdate: new Date(),
    });
    appDispatch({ type: actions.activeChat, id });
  };

  // once chats are loaded set the first chat as active chat
  useEffect(() => {
    if (
      chats &&
      chats.length > 0 &&
      // and also change activeChat if mode was changed
      (activeChat === '' || showChatsNoEnded !== lastShowChatsNoEnded)
    ) {
      if (!small) {
        appDispatch({ type: actions.activeChat, id: chats[0].id });
        setLastShowChatsNoEnded(showChatsNoEnded);
      }
    }
  }, [appDispatch, activeChat, chats, small, showChatsNoEnded, lastShowChatsNoEnded]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollTo(0, 0);
    }
  }, [page]);

  const getChatType = (chat) => {
    return chat.type === 1 ? (
      <WhatsAppIcon className={classes.whatsappIcon} />
    ) : (
      <LanguageIcon className={classes.webchatIcon} />
    );
  };

  const getName = (chat) => {
    let client = '';
    if (!isUndefined(chat.client)) {
      if (!isEmpty(chat.client.wspUsername)) {
        client = `${chat.client.wspUsername} `;
      }
      if (!isEmpty(chat.client.name)) {
        client = `${chat.client.name} `;
      }
      if (!isEmpty(chat.client.phone)) {
        client += `${client ? '- ' : ''}${chat.client.phone}`;
      }
    }
    return client;
  };

  useEffect(() => {
    if (chats.slice((page - 1) * 20, page * 20).length === 0 && page > 1) {
      setPage(page - 1);
    }
  }, [chats.length]);

  const generateMessageLabel = (message) => {
    if (message.contentType === 'file') return 'Documento PDF';
    if (message.contentType === 'audio') return 'Audio';
    else if (message.contentType === 'image') return 'Imagen';
    else return truncate(message.message, { length: 90 });
  };

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Paper ref={scrollRef} className={classes.paper} square>
        <List className={classes.list} pt={0} pb={0}>
          {chats &&
            chats.slice((page - 1) * 20, page * 20).map((chat) => {
              const statusInfo =
                chat.status === 3
                  ? {
                      label: 'terminado',
                      color: '#4EC04E',
                      key: 3,
                    }
                  : config && config.states && config.states.find((e) => e.key === chat.status);

              return (
                <Box key={chat.id}>
                  <Box
                    button
                    className={
                      activeChat === chat.id
                        ? `${classes.chatContainer}  ${classes.chatActive}`
                        : `${classes.chatContainer}`
                    }
                    onClick={(event) => handleClick(chat.id)}
                    selected
                    px={2}
                  >
                    <Typography variant="subtitle1" className={classes.chatSuperText}>
                      Último mensaje recibido: <Box sx={{ marginLeft: 'auto' }} />
                      {chat && chat.updatedAt && moment(chat.updatedAt.toDate()).fromNow()}
                    </Typography>
                    <Box className={classes.chipBox} m={0}>
                      {chat.botStopped ? <BlockIcon className={classes.blockedChat} /> : ''}
                      <Chip
                        label={(statusInfo && statusInfo.label) || ''}
                        className={classes.chips}
                        sx={{
                          backgroundColor: (statusInfo && statusInfo.color) || '#ffcc01',
                        }}
                      />
                    </Box>
                    <Box width="100%" pt={4} pb={3}>
                      <ListItemText
                        primary={
                          <span>
                            {getChatType(chat)} {getName(chat)}
                          </span>
                        }
                        secondary={
                          chat.messages.length > 0 ? generateMessageLabel(last(chat.messages)) : ''
                        }
                        secondaryTypographyProps={{
                          color: '#7a7a7a',
                        }}
                      />
                    </Box>
                  </Box>
                  <Divider />
                </Box>
              );
            })}
          {(!chats || chats.length === 0) && (
            <Box p={2}>
              <Typography>No hay ningún chat que mostrar</Typography>
            </Box>
          )}
        </List>
      </Paper>
      {chats && chats.length && chats.length > 20 ? (
        <Box display="flex" justifyContent="center" overflow="hidden" py={1}>
          <Pagination
            siblingCount={0}
            count={
              chats.length % 20 === 0
                ? parseInt(chats.length / 20)
                : parseInt(chats.length / 20) + 1
            }
            color="primary"
            page={page}
            onChange={(e, value) => setPage(value)}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default ChatList;
